import { getNow } from "@sg-protect/components";
import {
    type ExpirationResult,
    type SubscriptionChannel,
    getBannerSubscriptions,
    getExpiringTrialDaysLeft,
    resolveExpirationStatus,
    resolveSubscriptionChannel
} from "./layouts/components/subscription-expiration-banner/selectors.ts";
import { useSession } from "./SessionProvider.tsx";
import { useGovernanceTenantId } from "./useGovernanceTenantId.ts";

export interface SubscriptionBannersState {
    isExpirationBannerVisible: boolean;
    isBlockedBannerVisible: boolean;
    /** The read-only banner replaces these two rather than stacking with them, so it owns the same precedence. */
    isReadOnlyBannerVisible: boolean;
    /** Null whenever no expiration state applies, independently of whether the banner is suppressed. */
    expiration: ExpirationResult | null;
    /** Days remaining on the expiring trial, 0 for every other status. */
    trialDaysLeft: number;
    channel: SubscriptionChannel;
}

export function useSubscriptionBanners(): SubscriptionBannersState {
    const { workspaceUserRole, products } = useSession();
    const tenantId = useGovernanceTenantId();
    const { isProvidedByReseller } = products ?? {};
    const protectSubscription = products?.protect.subscription;
    const isValidProtectSubscription = protectSubscription?.isValid ?? false;
    const hasRemediationAddOn = protectSubscription?.hasRemediationAddOn ?? false;

    // Partners don't have products.subscription in the session yet, so there's nothing reliable to
    // base these banners on -- suppress them rather than risk showing wrong data.
    const isPartnerSession = products?.partner != null;

    const isReadOnlyBannerVisible = isValidProtectSubscription && tenantId != null && !hasRemediationAddOn;
    const bannerSubscriptions = getBannerSubscriptions(products);
    const now = getNow();
    const expiration = resolveExpirationStatus(bannerSubscriptions, now);
    const isBlocked = bannerSubscriptions.some(s => s.isBlocked);

    const isExpirationBannerVisible = !isReadOnlyBannerVisible && !isPartnerSession && expiration != null;
    const isBlockedBannerVisible = !isReadOnlyBannerVisible && !isPartnerSession && isBlocked;

    return {
        isExpirationBannerVisible,
        isBlockedBannerVisible,
        isReadOnlyBannerVisible,
        expiration,
        trialDaysLeft: expiration?.status === "expiring-trial" ? getExpiringTrialDaysLeft(expiration.trial, now) : 0,
        channel: resolveSubscriptionChannel({
            isProvidedByReseller: isProvidedByReseller ?? false,
            isWorkspaceAdmin: workspaceUserRole === "admin"
        })
    };
}
